import type { FormArray, FormControl, UntypedFormArray } from '@angular/forms';
import { DirectoryItemCategory, FmSourceType } from '@sendpulse/file-manager';
import type {
    NavigationWidgetData,
    NavigationWidgetDataFormType,
    MenuItem,
} from '@web/widgets/navigation/type/navigation-widget.type';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import {
    type CdkDragDrop,
    moveItemInArray,
    CdkDropList,
    CdkDrag,
    CdkDropListGroup,
    CdkDragHandle,
    CdkDragPlaceholder,
} from '@angular/cdk/drag-drop';

import { ERadioGroupType } from '@ui/sp-radio-group';
import { EButtonLinkType, type EFontStyle, EHorizontalPosition } from '@common/enums';

import { WebBaseControlComponent } from '@web/widgets/common/abstract/web-base-control.component';

import { navigationWidgetFormGroup } from '@web/widgets/navigation/form/navigation-widget.form';
import type { SubmenuItem } from '@web/widgets/menu/type';
import { createId } from '@paralleldrive/cuid2';
import { EControlHandleEvents } from '@ui/sp-control-handle/src/sp-control-handle.enum';
import { TranslateModule } from '@ngx-translate/core';
import { StyleSettingsComponent } from './style-settings/style-settings.component';
import { NavigationSettingsComponent } from './navigation-settings/navigation-settings.component';

import { SpControlHandleComponent } from '@ui/sp-control-handle';
import { WebSettingsModule } from '@web/shared/components/web-settings/web-settings.module';
import { CommonModule } from '@angular/common';
import { WebCustomIdComponent } from '@web/shared/components/web-custom-id/web-custom-id.component';

enum CurrentSetting {
    Main = 'main',
    ItemSettings = 'item-settings',
    StyleSettings = 'style-settings',
    SubmenuItemSettings = 'submenu-item-settings',
}

@Component({
    selector: 'web-navigation-control',
    templateUrl: './navigation-control.component.html',
    styleUrls: ['./navigation-control.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        WebSettingsModule,
        CdkDropList,
        CdkDrag,
        CdkDropListGroup,
        SpControlHandleComponent,
        CdkDragHandle,
        CdkDragPlaceholder,
        NavigationSettingsComponent,
        StyleSettingsComponent,
        TranslateModule,
        WebCustomIdComponent,
    ],
})
export class NavigationControlComponent extends WebBaseControlComponent<NavigationWidgetData> {
    public readonly ERadioGroupType = ERadioGroupType;
    public readonly FONT_SIZE_MAX = 72;
    public readonly EHorizontalPosition = EHorizontalPosition;
    public readonly DirectoryItemCategory = DirectoryItemCategory;

    public currentPanel: CurrentSetting = CurrentSetting.Main;

    public editableMenuItem: MenuItem = null;
    public editableMenuItemIndex: number = null;
    public editableSubmenuItem: SubmenuItem = null;
    public editableSubmenuItemIndex: number = null;
    public buttonDisabled: boolean = false;
    public editableMenuStyle: NavigationWidgetData = null;
    public readonly fmSourceType = FmSourceType;

    public trackIndex = (index: number): number => index;

    public controlHandleEvents = EControlHandleEvents;

    public readonly userId$ = this.userFacade.userId$;

    protected initForm(): void {
        this.controlForm = this.fb.group({
            data: navigationWidgetFormGroup(),
        });
    }

    protected setFormData(): void {
        this.menuList.clear({ emitEvent: false });
        this.fontStyle.clear({ emitEvent: false });
        this.submenuFontStyle.clear({ emitEvent: false });
        this.widgetData.menuList.forEach((menuItem: MenuItem) =>
            this.menuList.push(this.fb.control(menuItem), { emitEvent: false })
        );
        this.widgetData.fontStyle.forEach((item: EFontStyle) =>
            this.fontStyle.push(this.fb.control(item), { emitEvent: false })
        );
        this.widgetData.submenuFontStyle.forEach((item: EFontStyle) =>
            this.submenuFontStyle.push(this.fb.control(item), { emitEvent: false })
        );
        this.controlForm.patchValue(
            {
                data: this.widgetData,
            },
            { emitEvent: false }
        );

        this.editableMenuStyle = this.widgetData;
    }

    public get menuList(): UntypedFormArray {
        return this.data.get('menuList') as UntypedFormArray;
    }

    public get horizontalPosition(): FormControl<EHorizontalPosition> {
        return this.data.get('horizontalPosition') as FormControl<EHorizontalPosition>;
    }

    public get fontStyle(): NavigationWidgetDataFormType['fontStyle'] {
        return this.data.get('fontStyle') as NavigationWidgetDataFormType['fontStyle'];
    }

    public get submenuFontStyle(): NavigationWidgetDataFormType['submenuFontStyle'] {
        return this.data.get('submenuFontStyle') as NavigationWidgetDataFormType['submenuFontStyle'];
    }

    public addMenuItem(): void {
        this.menuList.push(
            this.fb.control({
                name: 'Menu item',
                value: 'https://example.com',
                linkType: EButtonLinkType.link,
                isVisible: true,
                id: createId(),
            })
        );
    }

    public copyMenuItem(menuItem: MenuItem, index: number): void {
        const newMenuItem: MenuItem = {
            ...menuItem,
            id: createId(),
        };

        this.menuList.insert(index + 1, this.fb.control(newMenuItem));

        this.changeDetectorRef.detectChanges();
    }

    public copySubmenuItem(parentMenuItem: MenuItem, submenuItem: SubmenuItem, index: number): void {
        const newSubmenuItem: MenuItem = {
            ...submenuItem,
            id: createId(),
        };

        const newSubmenuList = [...parentMenuItem.submenuList];
        newSubmenuList.splice(index, 0, newSubmenuItem);

        const parentIndex = [...this.menuList.value].findIndex((item) => item.id === parentMenuItem.id);

        this.menuList.at(parentIndex).patchValue({
            ...parentMenuItem,
            ...{ submenuList: newSubmenuList },
        });
    }

    public addSubmenuItem(index: number): void {
        const submenuItem: MenuItem = {
            name: 'Submenu item',
            linkUrl: 'https://example.com',
            linkType: EButtonLinkType.link,
            isVisible: true,
            id: createId(),
        };

        const newSubmenuList: SubmenuItem[] = [];

        const menuItem = this.menuList.at(index).value;

        if (!menuItem.submenuList?.length) {
            newSubmenuList.push(submenuItem);
        } else {
            for (let i = 0; i < menuItem.submenuList?.length; i++) {
                newSubmenuList.push(menuItem.submenuList[i]);
            }

            newSubmenuList.push(submenuItem);
        }

        this.menuList.at(index).patchValue({
            ...menuItem,
            ...{ submenuList: newSubmenuList },
        });
    }

    public removeMenuItem(i: number): void {
        if (this.menuList.value.length === 1) {
            return;
        }

        this.menuList.removeAt(i);
    }

    public removeSubmenuItem(parentMenuItem: MenuItem, i: number) {
        const newSubmenuList = [...parentMenuItem.submenuList];
        newSubmenuList.splice(i, 1);

        const parentIndex = [...this.menuList.value].findIndex((item) => item.id === parentMenuItem.id);

        this.menuList.at(parentIndex).patchValue({
            ...parentMenuItem,
            ...{ submenuList: newSubmenuList },
        });
    }

    public changeMenuItemVisibility(index: number): void {
        const menuItem = this.menuList.at(index).value;
        this.menuList.at(index).patchValue({
            ...menuItem,
            isVisible: !menuItem.isVisible,
        });
    }

    public changeSubmenuItemVisibility(parentMenuItem: MenuItem, submenuItem: SubmenuItem, i: number) {
        const newSubmenuItem: SubmenuItem = {
            ...submenuItem,
            isVisible: !submenuItem.isVisible,
        };

        const newSubmenuList = [...parentMenuItem.submenuList];
        newSubmenuList.splice(i, 1, newSubmenuItem);

        const parentIndex = [...this.menuList.value].findIndex((item) => item.id === parentMenuItem.id);

        this.menuList.at(parentIndex).patchValue({
            ...parentMenuItem,
            ...{ submenuList: newSubmenuList },
        });
    }

    public editMenuItem(menuItem: MenuItem, index: number): void {
        this.editableMenuItem = menuItem;
        this.editableMenuItemIndex = index;

        this.currentPanel = CurrentSetting.ItemSettings;
    }

    public editSubmenuItem(parentMenuItem: MenuItem, submenuItem: SubmenuItem, submenuIndex: number): void {
        this.editableMenuItem = parentMenuItem;
        this.editableSubmenuItem = submenuItem;
        this.editableSubmenuItemIndex = submenuIndex;
        this.editableMenuItemIndex = [...this.menuList.value].findIndex((item) => item.id === parentMenuItem.id);

        this.currentPanel = CurrentSetting.SubmenuItemSettings;
    }

    public saveMenuItem(data: MenuItem): void {
        this.menuList.at(this.editableMenuItemIndex).patchValue(data);
    }

    public saveSubmenuItem(submenuData: SubmenuItem): void {
        const newSubmenuList = [...this.editableMenuItem.submenuList];
        newSubmenuList.splice(this.editableSubmenuItemIndex, 1, submenuData);

        this.menuList.at(this.editableMenuItemIndex).patchValue({
            ...this.editableMenuItem,
            ...{ submenuList: newSubmenuList },
        });
    }

    public dropMenuItem(event: CdkDragDrop<FormArray>): void {
        if (event.previousIndex === event.currentIndex) {
            return;
        }

        const buttons = [...this.menuList.value];
        moveItemInArray(buttons, event.previousIndex, event.currentIndex);

        this.menuList.setValue(buttons);
    }

    public dropSubmenuItem(event: CdkDragDrop<FormArray>, parentMenuItem: MenuItem, index): void {
        if (event.previousIndex === event.currentIndex) {
            return;
        }

        const newSubmenuList = [...parentMenuItem.submenuList];
        moveItemInArray(newSubmenuList, event.previousIndex, event.currentIndex);

        this.menuList.at(index).patchValue({
            ...parentMenuItem,
            ...{ submenuList: newSubmenuList },
        });
    }

    public editMenuStyle(): void {
        this.currentPanel = CurrentSetting.StyleSettings;
    }

    public cancelEditSettings(): void {
        this.currentPanel = CurrentSetting.Main;
    }
}
